// src/pages/MainDashboard.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import authAxios from "../api/authAxios";

// ✅ Import stores
import { useAuthStore } from "../stores/authStore";
import { useOrganizationStore } from "../stores/organizationStore";
import { useUIStore } from "../stores/uiStore";

import OrgAdminView from "../components/dashboard/OrgAdminView";
import OrgCameraView from "../components/dashboard/OrgCameraView";

// ✅ Default API fallback
const API =
  process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : "https://vision.viewmax.net/api";

export default function MainDashboard() {
  // ==========================================
  // GET STATE FROM STORES (instead of localStorage/useState)
  // ==========================================
  
  // Auth store
  const currentUser = useAuthStore(s => s.currentUser);
  const setCurrentUser = useAuthStore(s => s.setCurrentUser);
  const activeOrgId = useAuthStore(s => s.activeOrgId);
  const activeSiteId = useAuthStore(s => s.activeSiteId);
  const setActiveOrg = useAuthStore(s => s.setActiveOrg);
  const setActiveSite = useAuthStore(s => s.setActiveSite);
  const logout = useAuthStore(s => s.logout);
  
  // Organization store
  const organizations = useOrganizationStore(s => s.organizations);
  const setOrganizations = useOrganizationStore(s => s.setOrganizations);
  
  // ==========================================
  // LOCAL STATE (keep only what's needed)
  // ==========================================
  const [ready, setReady] = useState(false);
  const [token, setToken] = useState(null);
  const [globalRole, setGlobalRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true); // ✅ Added loading state

  // ==========================================
  // INITIALIZE AUTH FROM LOCALSTORAGE
  // ==========================================
  useEffect(() => {
    const initAuth = async () => {
      // Wait slightly to ensure localStorage writes finish
      await new Promise((res) => setTimeout(res, 300));

      const access = localStorage.getItem("access");
      const role = localStorage.getItem("userGlobalRole");
      const id = localStorage.getItem("userId");
      const sessionTime = Number(localStorage.getItem("sessionTimestamp") || 0);

      // Allow login even if timestamp missing
      const MAX_SESSION_AGE = 24 * 60 * 60 * 1000; // 24 hours
      const expired = sessionTime && Date.now() - sessionTime > MAX_SESSION_AGE;

      if (!access || !role || !id) {
        console.warn("⚠️ Missing auth data, retrying in case of slow localStorage...");
        
        // 🔁 Retry once after a small wait before logging out
        await new Promise((res) => setTimeout(res, 400));
        const retryAccess = localStorage.getItem("access");
        const retryRole = localStorage.getItem("userGlobalRole");
        const retryId = localStorage.getItem("userId");

        if (!retryAccess || !retryRole || !retryId) {
          console.error("❌ Still missing auth data after retry. Redirecting to home.");
          localStorage.clear();
          window.location.replace("/");
          return;
        }

        // ✅ recovered on retry
        setToken(retryAccess);
        setGlobalRole(retryRole.toLowerCase());
        setUserId(Number(retryId));
        setReady(true);
        return;
      }

      if (expired) {
        console.warn("⚠️ Session expired — redirecting to home");
        localStorage.clear();
        window.location.replace("/");
        return;
      }

      // ✅ Everything valid
      setToken(access);
      setGlobalRole(role.toLowerCase());
      setUserId(Number(id));
      setReady(true);
    };

    initAuth();
  }, []);

  // ==========================================
  // FETCH CURRENT USER & SET DEFAULT SITE
  // ==========================================
useEffect(() => {
  if (!ready || !token) return;
  
  authAxios
    .get("/users/me/")
    .then((res) => {
      console.log("✅ currentUser fetched:", res.data);
      
      setCurrentUser(res.data);

      const defaultSite = res.data.site_memberships?.find(
        (m) => m.is_default
      );

      if (defaultSite) {
        //  Try multiple property names for organization ID

        const orgMembership = res.data.memberships?.find(
          (m) => m.organization.name === defaultSite.site.organization ||
               m.organization.id === defaultSite.site.organization
        );

        const orgId = orgMembership?.organization?.id;

        console.log("✅ Setting default site:", {
          orgId,
          siteId: defaultSite.site.id,
          siteName: defaultSite.site.name,
          rawSite: defaultSite.site  // Debug: see full structure
        });

        if (orgId) {
          setActiveOrg(orgId);
          setActiveSite(defaultSite.site.id);
          useUIStore.getState().setActiveSection("cameras");
      
          localStorage.setItem("activeOrgId", String(orgId));
          localStorage.setItem("activeSiteId", String(defaultSite.site.id));
        } else {
          console.error("❌ Could not find organization_id in site object:", defaultSite.site);
        }
      }
      setLoading(false);
    })
    .catch((err) => {
      console.error("❌ Failed to fetch current user:", err.response?.data || err.message);
      setLoading(false);
    });
}, [ready, token, setCurrentUser, setActiveOrg, setActiveSite]);

  // ==========================================
  // AUTH HEADERS
  // ==========================================
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // ==========================================
  // FETCH ORGANIZATIONS
  // ==========================================

const fetchOrganizations = async () => {
  try {
    let url = `${API}/organizations/`;
    if (globalRole !== "platform_admin") url = `${API}/organizations/?mine=1`;

    const res = await axios.get(url, { headers: authHeaders });
    console.log("✅ Orgs fetched:", res.data);

    let orgsData = res.data || [];

    // ✅ FIX: For org admins, fetch sites separately
    if (globalRole === "org_admin" && orgsData.length > 0) {
      console.log("🔄 Org admin - fetching sites separately...");
      
      // Fetch sites for each org
      const orgsWithSites = await Promise.all(
        orgsData.map(async (org) => {
          try {
            const sitesRes = await axios.get(
              `${API}/organizations/${org.id}/assignable-sites/`,
              { headers: authHeaders }
            );
            return {
              ...org,
              sites: sitesRes.data || []
            };
          } catch (err) {
            console.error(`Failed to fetch sites for org ${org.id}:`, err);
            return org;
          }
        })
      );
      
      orgsData = orgsWithSites;
      console.log("✅ Orgs with sites:", orgsData);
    }

    // ✅ Store in Zustand
    setOrganizations(orgsData);

    // 🧩 Auto-select first org if none selected
    if (!activeOrgId && orgsData.length > 0) {
      const firstOrg = orgsData[0];
      console.log("📌 Auto-selecting org:", firstOrg.name, firstOrg.id);
      setActiveOrg(firstOrg.id);
      localStorage.setItem("activeOrgId", String(firstOrg.id));
      
      // Also try to select first site
      if (firstOrg.sites && firstOrg.sites.length > 0) {
        const firstSite = firstOrg.sites[0];
        console.log("📌 Auto-selecting site:", firstSite.name, firstSite.id);
        setActiveSite(firstSite.id);
        localStorage.setItem("activeSiteId", String(firstSite.id));
      }
    }
  } catch (e) {
    console.error("❌ fetchOrganizations failed:", e?.response?.data || e.message);
    if (e?.response?.status === 401) {
      localStorage.clear();
      window.location.replace("/");
    }
  }
};



  const v1_fetchOrganizations = async () => {
    try {
      let url = `${API}/organizations/`;
      if (globalRole !== "platform_admin") url = `${API}/organizations/?mine=1`;

      const res = await axios.get(url, { headers: authHeaders });
      console.log("✅ Orgs fetched:", res.data);

      // ✅ Store in Zustand instead of local state
      setOrganizations(res.data || []);

      // 🧩 Auto-select first org if none selected (backup logic)
      if (!activeOrgId && res.data?.length > 0) {
        const firstOrg = res.data[0];
        console.log("📌 Auto-selecting first org (no default site):", firstOrg.name, firstOrg.id);
        setActiveOrg(firstOrg.id);
        localStorage.setItem("activeOrgId", String(firstOrg.id));
        
        // Also try to select first site
        if (firstOrg.sites && firstOrg.sites.length > 0) {
          const firstSite = firstOrg.sites[0];
          console.log("📌 Auto-selecting first site:", firstSite.name, firstSite.id);
          setActiveSite(firstSite.id);
          localStorage.setItem("activeSiteId", String(firstSite.id));
        }
      }
    } catch (e) {
      console.error("❌ fetchOrganizations failed:", e?.response?.data || e.message);
      if (e?.response?.status === 401) {
        localStorage.clear();
        window.location.replace("/");
      }
    }
  };

  // Fetch orgs when ready
  useEffect(() => {
    if (!ready || !token) return;
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, token, globalRole]);

  // ==========================================
  // AUTO-SELECT ORG FOR ORG_ADMIN (backup)
  // ==========================================
  useEffect(() => {
    if (globalRole === "org_admin" && organizations.length > 0 && !activeOrgId) {
      const firstOrg = organizations[0];
      if (firstOrg) {
        setActiveOrg(firstOrg.id);
        localStorage.setItem("activeOrgId", String(firstOrg.id));
        
        // Also select first site if available
        if (firstOrg.sites && firstOrg.sites.length > 0) {
          setActiveSite(firstOrg.sites[0].id);
          localStorage.setItem("activeSiteId", String(firstOrg.sites[0].id));
        }
      }
    }
  }, [globalRole, organizations, activeOrgId, setActiveOrg, setActiveSite]);

  // ==========================================
  // ORGANIZATION CRUD (PLATFORM ADMIN ONLY)
  // ==========================================
  
  const onAddOrg = async () => {
    const name = prompt("Enter organization name:");
    if (!name) return;

    try {
      await authAxios.post("/organizations/", { name });
      await fetchOrganizations();
    } catch (err) {
      console.error("❌ Failed to add organization:", err.response?.data || err);
      alert("Failed to add organization");
    }
  };

  const onEditOrg = async (org) => {
    const name = prompt("Edit organization name:", org.name);
    if (!name) return;

    try {
      await authAxios.patch(`/organizations/${org.id}/`, { name });
      await fetchOrganizations();
    } catch (err) {
      console.error("❌ Failed to edit organization:", err.response?.data || err);
      alert("Failed to edit organization");
    }
  };

  const onDeleteOrg = async (orgId) => {
    if (!window.confirm("Delete this organization?")) return;

    try {
      await authAxios.delete(`/organizations/${orgId}/`);
      await fetchOrganizations();
    } catch (err) {
      console.error("❌ Failed to delete organization:", err.response?.data || err);
      alert("Failed to delete organization");
    }
  };

  // ==========================================
  // USER MANAGEMENT
  // ==========================================
  
  const onAddUser = async (orgId, payload) => {
    try {
      // 1️⃣ Create user (backend auto-handles org membership for org_admins)
      const userRes = await authAxios.post(
        "/users/",
        {
          email: payload.email,
          name: payload.name,
          password: payload.password || undefined,
          global_role: payload.global_role || "platform_user",
          org_role: payload.org_role || "org_user",
          organization: orgId,
        }
      );

      const newUserId = userRes.data.id;

      // 2️⃣ Platform admins may explicitly create org membership
      const currentUserRole = currentUser?.global_role;
      if (currentUserRole === "platform_admin") {
        await authAxios.post("/memberships/", {
          organization: orgId,
          user: newUserId,
          role: payload.org_role || "org_user",
        });
      }

      // 3️⃣ 🔥 Persist SITE memberships
      if (Array.isArray(payload.sites)) {
        await authAxios.post(
          `/users/${newUserId}/sites/`,
          { 
            sites: payload.sites,
            default_site_id: payload.default_site_id ?? null,
          }
        );
      }

      // 4️⃣ Refresh UI
      await fetchOrganizations();
    } catch (err) {
      console.error("❌ Error adding user:", err.response?.data || err);
      alert(err.response?.data?.detail || "Error adding user");
    }
  };

  const onEditUser = async (orgId, payload) => {
    console.warn("🔥🔥🔥 MainDashboard.onEditUser", payload);
    const userId = payload.id || payload.user_id || payload.user?.id;
    if (!userId) {
      console.error("❌ Missing user ID in onEditUser payload:", payload);
      alert("Cannot edit user — missing user ID.");
      return;
    }

    try {
      // 1️⃣ Update base user fields
      const userUpdate = {};
      if (payload.email) userUpdate.email = payload.email;
      if (payload.name) userUpdate.name = payload.name;
      if (payload.password) userUpdate.password = payload.password;
      if (payload.global_role) userUpdate.global_role = payload.global_role;

      if (Object.keys(userUpdate).length > 0) {
        await authAxios.put(`/users/${userId}/`, userUpdate);
      }

      // 2️⃣ Update org membership role
      if (payload.org_role) {
        const res = await authAxios.get(
          `/memberships/?organization=${orgId}&user=${userId}`
        );
        const m = res.data?.[0];
        if (m) {
          await authAxios.put(`/memberships/${m.id}/`, {
            organization: orgId,
            user: userId,
            role: payload.org_role,
          });
        }
      }

      // 3️⃣ ✅ SITE MEMBERSHIP SYNC
      if (Array.isArray(payload.sites)) {
        console.log("🔥 SENDING SITE SYNC", payload.sites);
        await authAxios.post(
          `/users/${userId}/sites/`,
          { 
            sites: payload.sites, 
            default_site_id: payload.default_site_id ?? null,
          }
        );
      }

      await fetchOrganizations();
    } catch (err) {
      console.error("❌ Error editing user:", err.response?.data || err);
      alert(err.response?.data?.detail || "Error editing user");
    }
  };

  const onDeleteUser = async (orgId, userIdParam) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      // 🔹 Step 1: Try membership delete first (for org admins)
      try {
        const res = await axios.get(
          `${API}/memberships/?organization=${orgId}&user=${userIdParam}`,
          { headers: authHeaders }
        );
        const m = (res.data || [])[0];
        if (m) {
          await axios.delete(`${API}/memberships/${m.id}/`, { headers: authHeaders });
          console.log(`✅ Removed membership ${m.id} for user ${userIdParam}`);
        }
      } catch (err) {
        console.warn("⚠️ Membership deletion skipped or failed:", err.response?.status);
      }

      // 🔹 Step 2: Try deleting user (only platform admins will succeed)
      try {
        await axios.delete(`${API}/users/${userIdParam}/`, { headers: authHeaders });
        console.log(`✅ Deleted user ${userIdParam}`);
      } catch (err) {
        if (err.response?.status === 403) {
          console.log("ℹ️ Org admin cannot delete full user account, only membership removed.");
        } else {
          throw err;
        }
      }

      await fetchOrganizations();
    } catch (err) {
      console.error("❌ Error deleting user:", err.response?.data || err);
      alert("Error deleting user.");
    }
  };

  // ==========================================
  // SITE MANAGEMENT
  // ==========================================
  
  const onAddSite = async (orgId, payload) => {
    await axios.post(`${API}/organizations/${orgId}/sites/`, payload, { headers: authHeaders });
    await fetchOrganizations();
  };

  const onEditSite = async (orgId, payload) => {
    await axios.put(
      `${API}/organizations/${orgId}/sites/${payload.id}/`,
      payload,
      { headers: authHeaders }
    );
    await fetchOrganizations();
  };

  const onDeleteSite = async (orgId, siteId) => {
    if (!window.confirm("Delete this site?")) return;
    await axios.delete(`${API}/organizations/${orgId}/sites/${siteId}/`, { headers: authHeaders });
    await fetchOrganizations();
  };

  // ==========================================
  // LOGOUT HANDLER
  // ==========================================
  
  const handleLogout = () => {
    logout(); // Clears Zustand store
    localStorage.clear();
    window.location.replace("/");
  };

  // ==========================================
  // LOADING STATE
  // ==========================================
  
  if (!ready || loading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-400">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <div>Loading dashboard…</div>
        </div>
      </div>
    );
  }

  // ==========================================
  // WORKSPACE (ALL ROLES)
  // ==========================================
  
  // ✅ Debug log
  console.log("🎯 Render decision:", {
    activeOrgId,
    activeSiteId,
    hasOrganizations: organizations.length > 0,
    currentUser: currentUser?.email
  });

  if (globalRole=== "platform_admin" || activeOrgId) {
    return (
      <OrgCameraView
        organizations={organizations}
        activeOrgId={activeOrgId}
        setActiveOrgId={(id) => {
          setActiveOrg(id);
          localStorage.setItem("activeOrgId", String(id));
        }}
        activeSiteId={activeSiteId}
        setActiveSiteId={(id) => {
          setActiveSite(id);
          localStorage.setItem("activeSiteId", String(id));
        }}
        global_role={globalRole}
        currentUser={currentUser}

        onAddUser={onAddUser}
        onEditUser={onEditUser}
        onDeleteUser={onDeleteUser}

        onAddSite={onAddSite}
        onEditSite={onEditSite}
        onDeleteSite={onDeleteSite}

        onRefresh={fetchOrganizations}

        onAddOrg={onAddOrg}
        onEditOrg={onEditOrg}
        onDeleteOrg={onDeleteOrg}

        onLogout={handleLogout}
      />
    );
  }

  // ==========================================
  // DEFAULT FALLBACK
  // ==========================================
  
  return (
    <div className="flex items-center justify-center h-screen text-gray-500">
      <div className="text-center">
        <p className="text-xl mb-4">You do not have access to this dashboard.</p>
        <p className="text-sm mb-4">No default site found.</p>
        <button 
          onClick={handleLogout}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Logout
        </button>
      </div>
    </div>
  );


}
